import { useMemo, type FocusEvent, type KeyboardEvent } from "react";
import { getClickableInstructionsId } from "./BoardKeyboardHelp";
import type { BoardRoving } from "./boardRoving";
import {
    useBoardCardKeys,
    type UseBoardCardKeysArgs,
} from "./useBoardCardKeys";
import { useBoardFocusedKey } from "./useBoardFocusedKey";
import {
    useBoardRovingFocus,
    type UseBoardRovingFocusArgs,
} from "./useBoardRovingFocus";

interface UseBoardLayoutFocusArgs<TItem>
    extends
        Omit<UseBoardRovingFocusArgs<TItem>, "focusedKey" | "onExtend">,
        Pick<UseBoardCardKeysArgs<TItem>, "cardShortcuts"> {
    /** Lanes kept mounted for a drag; the focused and active cards' lanes join them. */
    pinnedLaneIds: readonly string[];
    /** The element holding the keyboard instructions. */
    instructionsId: string;
    initialFocusedItemId?: string;
}

const getUniqueLaneIds = (laneIds: ReadonlyArray<string | null>) => [
    ...new Set(laneIds.flatMap((laneId) => (laneId === null ? [] : [laneId]))),
];

/**
 * Focus for `BoardLayout`: which card has focus, the roving Tab stop, the
 * lanes a windowed board keeps mounted for them, and the handlers for the
 * board root, with the focused card's selection keys and shortcuts.
 */
export const useBoardLayoutFocus = <TItem>({
    pinnedLaneIds,
    instructionsId,
    cardShortcuts,
    initialFocusedItemId,
    ...args
}: UseBoardLayoutFocusArgs<TItem>) => {
    const { focusedKey, focusedLaneId, focusHandlers, clearFocusedKey } =
        useBoardFocusedKey({
            cells: args.cells,
            columns: args.columns,
            lanes: args.lanes,
            isDragActive: args.isDragActive,
            initialFocusedItemId,
        });
    const cardKeys = useBoardCardKeys({ ...args, cardShortcuts });
    const roving = useBoardRovingFocus({
        ...args,
        focusedKey,
        onExtend: cardKeys.onExtend,
    });
    const activeKey = roving.activeSpot?.placementKey ?? null;
    const activeLaneId = roving.activeSpot?.placement.laneId ?? null;
    const lanePins = useMemo(
        () => getUniqueLaneIds([...pinnedLaneIds, focusedLaneId, activeLaneId]),
        [pinnedLaneIds, focusedLaneId, activeLaneId],
    );
    const rovingContext = useMemo(
        (): BoardRoving => ({
            focusedKey,
            activeKey,
            focusTargetKey: roving.focusTargetKey,
            describedById: instructionsId,
            clickableDescribedById: getClickableInstructionsId(instructionsId),
        }),
        [focusedKey, activeKey, roving.focusTargetKey, instructionsId],
    );
    const sectionHandlers = {
        onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
            roving.handlers.onKeyDown(event);
            cardKeys.onKeyDown(event);
        },
        onFocus: (event: FocusEvent<HTMLElement>) => {
            focusHandlers.onFocus(event);
            roving.handlers.onFocus(event);
        },
        onBlur: (event: FocusEvent<HTMLElement>) => {
            focusHandlers.onBlur(event);
            roving.handlers.onBlur(event);
        },
    };
    return {
        focusedKey,
        clearFocusedKey,
        lanePins,
        rovingContext,
        sectionHandlers,
        isHintShown: roving.isHintShown,
    };
};
